// src/components/auth/Signup.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import FilterDropdown from '../common/FilterDropdown';



const Signup = () => {
    const [formData, setFormData] = useState({
        role: 'student',
        firstName: '',
        lastName: '',
        email: '',
        studentId: '',
        teacherId: '',
        dateOfBirth: '',
        gender: '',
        grade: '',
        password: '',
        confirmPassword: '',
        careerPathway: '',
        stream: '',
        school: '',
    });
    const [schools, setSchools] = useState([]);
    const [loadingSchools, setLoadingSchools] = useState(false);
    const [grades, setGrades] = useState([]);
    const [loadingGrades, setLoadingGrades] = useState(false);
    const [streams, setStreams] = useState([]);
    const [loadingStreams, setLoadingStreams] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();
    const { post, get } = useApi();

    const streamOptions = React.useMemo(() => streams.map(stream => ({ name: stream.name, value: stream.id })), [streams]);

    React.useEffect(() => {
        const fetchInitialData = async () => {
            setLoadingSchools(true);
            try {
                const schoolsData = await get('/api/schools/');
                setSchools(schoolsData || []);
            } catch (err) {
                console.error('Error fetching schools:', err);
            } finally {
                setLoadingSchools(false);
            }
        };
        fetchInitialData();
    }, [get]);

    const fetchGrades = async (schoolId) => {
        setLoadingGrades(true);
        try {
            const gradesData = await get(`/api/cbc/grade-levels/?school_id=${schoolId}`);
            setGrades(gradesData || []);
        } catch (err) {
            console.error('Error fetching grades:', err);
        } finally {
            setLoadingGrades(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (name === 'school') {
            setGrades([]);
            setStreams([]);
            setFormData(prev => ({ ...prev, grade: '', stream: '' }));
            if (value) {
                fetchGrades(value);
            }
        }

        // Fetch streams if grade changes
        if (name === 'grade' && value) {
            fetchStreams(value, formData.school || (name === 'school' ? value : ''));
        }
    };

    const fetchStreams = async (gradeId, schoolId) => {
        setLoadingStreams(true);
        try {
            const streamsData = await get(`/api/cbc/streams/?grade_level=${gradeId}&school_id=${schoolId}`);
            setStreams(streamsData);
        } catch (err) {
            console.error('Error fetching streams:', err);
        } finally {
            setLoadingStreams(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.role === 'student' && streams.length > 0 && !formData.stream) {
            setError('Please select a stream for this grade.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                role: formData.role,
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
                password: formData.password,
                school: formData.school,
            };

            if (formData.role === 'student') {
                Object.assign(payload, {
                    student_id: formData.studentId,
                    date_of_birth: formData.dateOfBirth,
                    gender: formData.gender,
                    grade: formData.grade,
                    phone: formData.phone,
                    stream: formData.stream || null,
                    career_pathway: formData.grade === 'Grade 10' ? formData.careerPathway : null,
                });
            } else {
                payload.phone = formData.phone;
            }
            await post('/api/auth/register/', payload);
            navigate('/login');
        } catch (err) {
            setFieldErrors({});
            let errorMessage = 'Signup failed. Please try again.';
            if (err.response?.data) {
                const data = err.response.data;
                if (typeof data === 'string') {
                    errorMessage = data;
                } else if (data.detail) {
                    errorMessage = data.detail;
                } else if (data.error) {
                    errorMessage = data.error;
                } else if (data.non_field_errors) {
                    errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors.join(' ') : data.non_field_errors;
                } else {
                    const fields = {};
                    for (const [key, value] of Object.entries(data)) {
                        fields[key] = Array.isArray(value) ? value.join(' ') : value;
                    }
                    setFieldErrors(fields);
                    errorMessage = 'Please correct the errors below.';
                }
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4 md:p-6 lg:p-12 relative overflow-hidden" style={{ background: 'linear-gradient(150deg, #FFFFFF 0%, #F5D97A 100%)', backgroundAttachment: 'fixed' }}>
            {/* Soft Ambient Particles - Hidden on mobile for performance */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none hidden lg:block">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-white/40 rounded-full blur-[120px] animate-pulse" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-white/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '3s' }} />
            </div>

            <div className="w-full max-w-4xl relative z-10 transition-all duration-1000 animate-in fade-in slide-in-from-bottom-12 my-12">
                {/* Logo and Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center p-2 mb-2 group transition-all hover:scale-110 active:scale-95">
                        <img 
                            src="/kianda-school-logo-removebg-preview.png" 
                            alt="Kianda School Logo" 
                            className="h-24 lg:h-32 w-auto drop-shadow-2xl"
                        />
                    </div>
                    <h1 className="text-4xl lg:text-5xl font-black text-[#18216D] mb-1 tracking-tight">Create Account</h1>
                    <p className="text-[#18216D]/60 font-bold text-sm lg:text-base">
                        Join the Kianda School portal. Fill in your details to get started.
                    </p>
                </div>

                <div className="bg-white/70 backdrop-blur-3xl rounded-[2rem] lg:rounded-[3rem] p-6 md:p-8 lg:p-14 shadow-[0_32px_120px_-20px_rgba(245,217,122,0.15)] border border-white/60 relative overflow-hidden group/card">
                    <div className="mb-10">
                        <h2 className="text-3xl font-black text-[#18216D]">Register</h2>
                        <p className="text-slate-400 font-bold text-sm mt-2 font-mono uppercase tracking-[0.1em]">Step 1: Role Selection</p>
                    </div>

                    {error && (
                        <div className="mb-8 p-4 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl text-sm font-bold flex items-center gap-3">
                            <i className="fas fa-circle-exclamation"></i>
                            {error}
                        </div>
                    )}

                    <form className="space-y-8" onSubmit={handleSubmit}>
                        {/* Role Picker */}
                        <div className="grid grid-cols-2 gap-4">
                            {['student', 'teacher'].map((role) => (
                                <button
                                    key={role}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, role }))}
                                    className={`py-4 rounded-2xl border-2 transition-all flex flex-col items-center justify-center gap-2 ${formData.role === role
                                        ? 'border-[#18216D] bg-indigo-50/50 text-[#18216D]'
                                        : 'border-slate-50 bg-slate-50 text-slate-400 hover:border-slate-200'
                                        }`}
                                >
                                    <i className={`fas ${role === 'student' ? 'fa-user-graduate' : 'fa-chalkboard-user'
                                        } text-lg`}></i>
                                    <span className="text-[10px] font-black uppercase tracking-widest">{role}</span>
                                </button>
                            ))}
                        </div>

                        <div className="pt-4 border-t border-slate-50">
                            <p className="text-slate-400 font-bold text-sm mb-6 font-mono uppercase tracking-[0.1em]">Step 2: Personal Info</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">First Name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-medium text-[#18216D]"
                                        placeholder="First Name"
                                    />
                                    {fieldErrors.first_name && <p className="text-rose-500 text-[10px] mt-1 ml-1 font-bold">{fieldErrors.first_name}</p>}
                                </div>
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">Last Name</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-medium text-[#18216D]"
                                        placeholder="Last Name"
                                    />
                                    {fieldErrors.last_name && <p className="text-rose-500 text-[10px] mt-1 ml-1 font-bold">{fieldErrors.last_name}</p>}
                                </div>
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">Email Address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-medium text-[#18216D]"
                                        placeholder="Email"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        required
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-medium text-[#18216D]"
                                        placeholder="Phone"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-slate-50">
                            <p className="text-slate-400 font-bold text-sm mb-6 font-mono uppercase tracking-[0.1em]">Step 3: School Info</p>

                            {/* School Selection - Required for multi-tenancy */}
                            <div className="grid grid-cols-1 gap-6 mb-8">
                                <div className="md:col-span-2">
                                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">
                                        Select Your School {loadingSchools && <i className="fas fa-spinner fa-spin ml-2"></i>}
                                    </label>
                                    <select
                                        name="school"
                                        required
                                        value={formData.school}
                                        onChange={handleChange}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-bold text-[#18216D] appearance-none cursor-pointer"
                                    >
                                        <option value="">Choose School</option>
                                        {schools.map(school => (
                                            <option key={school.id} value={school.id}>{school.name}</option>
                                        ))}
                                    </select>
                                    {schools.length === 0 && !loadingSchools && (
                                        <p className="text-[9px] text-rose-400 mt-2 ml-1 italic font-bold">No schools available in the system.</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {formData.role === 'student' && (
                                    <>
                                        <div>
                                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">Student ID</label>
                                            <input
                                                type="text"
                                                name="studentId"
                                                required
                                                value={formData.studentId}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-medium text-[#18216D]"
                                                placeholder="Student ID"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">
                                                Grade {loadingGrades && <i className="fas fa-spinner fa-spin ml-2"></i>}
                                            </label>
                                            <FilterDropdown
                                                value={formData.grade}
                                                onChange={(val) => handleChange({ target: { name: 'grade', value: val } })}
                                                options={grades.map(g => ({ name: g.name, value: g.id }))}
                                                placeholder="Select Grade"
                                            />
                                        </div>

                                        {streams.length > 0 && (
                                            <div>
                                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">
                                                    Select Stream {loadingStreams && <i className="fas fa-spinner fa-spin ml-2"></i>}
                                                </label>
                                                <FilterDropdown
                                                    value={formData.stream}
                                                    onChange={(val) => handleChange({ target: { name: 'stream', value: val } })}
                                                    options={streamOptions}
                                                    placeholder="Select Stream"
                                                />
                                            </div>
                                        )}
                                        {grades.find(g => String(g.id) === String(formData.grade))?.name === 'Grade 10' && (
                                            <div className="md:col-span-2">
                                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">Career Pathway (G10 Selection)</label>
                                                <select
                                                    name="careerPathway"
                                                    required
                                                    value={formData.careerPathway}
                                                    onChange={handleChange}
                                                    className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-bold text-[#18216D] appearance-none cursor-pointer"
                                                >
                                                    <option value="">Choose Pathway</option>
                                                    <option value="STEM">Science, Technology, Engineering and Math (STEM)</option>
                                                    <option value="SOCIAL_SCIENCES">Social Sciences (Humanities & Languages)</option>
                                                    <option value="ARTS_SPORTS">Arts and Sports Science</option>
                                                </select>
                                                <p className="text-[9px] text-slate-400 mt-2 ml-1 italic font-bold">This selection will guide your Learning Area recommendations.</p>
                                            </div>
                                        )}
                                    </>
                                )}




                            </div>
                        </div>

                        <div className="pt-4 border-t border-slate-50">
                            <p className="text-slate-400 font-bold text-sm mb-6 font-mono uppercase tracking-[0.1em]">Step 4: Password</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">Password</label>
                                    <div className="relative group/field">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            required
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-medium text-[#18216D]"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center text-slate-300 hover:text-[#18216D] transition-colors"
                                        >
                                            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-xs`}></i>
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">Confirm Password</label>
                                    <div className="relative group/field">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            required
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-medium text-[#18216D]"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center text-slate-300 hover:text-[#18216D] transition-colors"
                                        >
                                            <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-xs`}></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-5 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-900/20 hover:bg-[#0D164F] hover:-translate-y-1 transition-all flex items-center justify-center gap-3 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-circle-notch fa-spin"></i>
                                    Registering...
                                </>
                            ) : (
                                <>
                                    <span>Register</span>
                                    <i className="fas fa-check-circle"></i>
                                </>
                            )}
                        </button>
                    </form>
                    <div className="mt-10 pt-10 border-t border-slate-100/50 text-center">
                        <p className="text-slate-400 text-sm font-medium">
                            Already have an account? <Link to="/login" className="text-[#18216D] font-black hover:underline underline-offset-4">Sign In Instead</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Signup;
